/**
 * The browsing-time fit verdicts — the first file in `src/rules/` (Phase 4,
 * plan decision 2). `fitAgainst` answers, for one advertised shift and one
 * viewer's effective roster: could this person actually work it, and if not
 * quite, why not?
 *
 * The verdict is *load-bearing* (`PLAN.md` §3.3): an advertised D or PM has
 * two clean partner blocks out of eight, not four, and a feed that doesn't
 * say why a shift doesn't fit — in hours, on the card — is a feed that lets
 * someone confirm a swap they cannot work. Every non-clean verdict therefore
 * carries the data its sentence needs, and `formatFitVerdict` builds every
 * sentence: no caller composes a reason by hand, so no surface can show an
 * unexplained one (CLAUDE.md's "never show an unexplained warning").
 *
 * Precedence, in order — a shift earlier in the list always wins:
 *
 * 1. **same-day rostered shift → BLOCK.** Any shift the viewer already works
 *    on the offered date, including the AM+N same-day pair that pure overlap
 *    maths would let through (PLAN §7 rule 2 exists precisely for that pair).
 * 2. **exception on the date → BLOCK**, all three kinds. `NOT_WORKING` is the
 *    same statement as LEAVE — "I am not working that day" — and a fit engine
 *    that lets you take a shift on a day you deliberately opted out of is a
 *    bug with a UI (the plan's drafting ruling).
 * 3. **overlap with any shift in the ±2-day window → BLOCK.** A shift that
 *    collides with one you are already working is physically impossible, no
 *    matter what the rest rule says about warnings (CLAUDE.md bite #3). A
 *    blocked swap never shows a mere warning.
 * 4. **rest gap under 10h → WARN**, reporting the worse of the two
 *    neighbouring gaps. A2 says the rest rule always warns and never blocks.
 *
 * Phase 6 remains the full rules phase: the complete gap-matrix fixtures, the
 * cross-user verdict SQL function, and running these same rules at
 * compose-time and inside the confirm transaction as the authority. This
 * module is shaped to be reused there, not replaced.
 *
 * **Purity.** No DOM, no React, no Supabase, no I/O — the `src/rules/`
 * import constraint (engine/, data/, rules/ only) applies and
 * `boundaries.test.ts` enforces it. Roster times arrive via the `config`
 * argument, never imported: the threshold and the precedence live here, the
 * arithmetic lives in `engine/shift-time` (`shiftInterval`/`gapMinutes`,
 * whose negative result *is* the overlap), the times themselves live in
 * `data/roster-config`.
 */

import { dayNumber } from '../engine/civil'
import { gapMinutes, shiftInterval } from '../engine/shift-time'
import type {
  DayException,
  IsoDate,
  RosterConfig,
  RosterShift,
  ShiftCode,
  ShiftInstance,
} from '../engine/types'

/** The 10-hour rest rule, A2. Warn at or above this gap is a clean fit. */
export const MIN_REST_MINUTES = 600

/** The shift being considered, as the feed sees it: a date and a code. */
export interface FitShift {
  date: IsoDate
  code: ShiftCode
}

/**
 * The viewer, as `feed.ts` assembles them: their effective roster over the
 * feed window (+1 day each side, so the neighbours of a boundary ad are
 * visible), plus their exception days. Explicit shifts and derived ones are
 * indistinguishable here on purpose — fit knows no staff type, the
 * one-code-path criterion extended to the rules layer (fixture
 * `EXPLICIT_VIEWER`).
 */
export interface FitViewer {
  shifts: readonly ShiftInstance[]
  exceptions: readonly DayException[]
}

/** Why a shift cannot be worked at all. */
export type BlockReason =
  | { reason: 'SAME_DAY'; otherCode: ShiftCode }
  | { reason: 'EXCEPTION'; exceptionKind: DayException['kind'] }
  | {
      reason: 'OVERLAP'
      otherCode: ShiftCode
      /**
       * Which side of the collision the viewer's shift is on: earlier means
       * its *end* falls inside the offered shift ("overlaps your N, ends
       * 07:00"); later means its *start* does ("overlaps your AM, starts
       * 06:30"). The formatter needs to know which boundary to name.
       */
      viewerShiftEarlier: boolean
    }

export type FitVerdict =
  | { kind: 'CLEAN' }
  | {
      kind: 'WARN'
      /** The short gap, in minutes — always positive (negatives are overlaps). */
      gapMin: number
      /**
       * `true` when the short rest sits *before* the viewer's shift — the
       * offered shift ends and the viewer's next one starts too soon
       * ("9.5h rest before your AM"). `false` when the viewer's shift ends
       * and the offered one starts too soon after it ("2h rest after your
       * N"). §3.3's worked cases, both directions.
       */
      before: boolean
      otherCode: ShiftCode
    }
  | ({ kind: 'BLOCK' } & BlockReason)

/**
 * The one verdict function. See the module header for the precedence order;
 * the numbered blocks below follow it exactly.
 */
export function fitAgainst(
  viewer: FitViewer,
  shift: FitShift,
  config: RosterConfig,
): FitVerdict {
  const offered = rosterShiftFor(shift.code, config)

  // 1. Same-day rostered shift. Any shift on the date blocks — including a
  // pair that doesn't overlap (AM + N the same day), which is PLAN §7
  // rule 2's own case and would slip through steps 3 and 4 below.
  const sameDay = viewer.shifts.find((candidate) => candidate.date === shift.date)
  if (sameDay !== undefined) {
    return { kind: 'BLOCK', reason: 'SAME_DAY', otherCode: sameDay.code }
  }

  // 2. Exception on the date. All three kinds block; the reason names the
  // kind so the sentence can too.
  const exception = viewer.exceptions.find((candidate) => candidate.date === shift.date)
  if (exception !== undefined) {
    return { kind: 'BLOCK', reason: 'EXCEPTION', exceptionKind: exception.kind }
  }

  // 3 & 4. Overlap and rest against every viewer shift near enough in time
  // to matter. A shift never reaches further than its own start date and its
  // end time (< 24h), so ±2 days covers every pair whose gap can be under
  // 10h — and dayNumber keeps the comparison one integer subtraction.
  const offeredInterval = shiftInterval(shift.date, offered)
  const offeredDay = dayNumber(shift.date)

  let overlap: ({ kind: 'BLOCK' } & Extract<BlockReason, { reason: 'OVERLAP' }>) | null = null
  let warn: Extract<FitVerdict, { kind: 'WARN' }> | null = null

  for (const viewerShift of viewer.shifts) {
    const delta = dayNumber(viewerShift.date) - offeredDay
    if (delta < -2 || delta > 2) continue

    const viewerInterval = shiftInterval(viewerShift.date, rosterShiftFor(viewerShift.code, config))
    const viewerEarlier = viewerInterval.startAbs <= offeredInterval.startAbs
    const gap = gapMinutes(
      viewerEarlier ? viewerInterval : offeredInterval,
      viewerEarlier ? offeredInterval : viewerInterval,
    )

    if (gap < 0) {
      // Physically impossible. First overlap wins; precedence over any warn
      // is by construction (a pair cannot be both negative and short-but-
      // positive, and step 3 outranks step 4 for different pairs).
      overlap ??= {
        kind: 'BLOCK',
        reason: 'OVERLAP',
        otherCode: viewerShift.code,
        viewerShiftEarlier: viewerEarlier,
      }
      continue
    }

    if (gap < MIN_REST_MINUTES && (warn === null || gap < warn.gapMin)) {
      warn = {
        kind: 'WARN',
        gapMin: gap,
        // The rest sits before the viewer's shift when the viewer's shift is
        // the later one of the pair.
        before: !viewerEarlier,
        otherCode: viewerShift.code,
      }
    }
  }

  if (overlap !== null) return overlap
  if (warn !== null) return warn
  return { kind: 'CLEAN' }
}

// ---------------------------------------------------------------------------
// The sentences. One formatter, exported, used by FitBadge — no caller builds
// a reason string by hand.
// ---------------------------------------------------------------------------

/**
 * Minutes-since-midnight as `'07:00'` — the one formatter this file needs and
 * cannot import: Station Ledger's `formatTime` lives in `src/ui/format`,
 * which `rules/` may not reach (the boundary `boundaries.test.ts` enforces).
 * Same output shape, four bytes of arithmetic.
 */
function formatMinutes(minute: number): string {
  const hour = Math.floor(minute / 60)
  const minuteOfHour = minute % 60
  return `${String(hour).padStart(2, '0')}:${String(minuteOfHour).padStart(2, '0')}`
}

/** Minutes as hours to one decimal — `570` → `'9.5h'`, `120` → `'2.0h'`. */
function formatHours(minutes: number): string {
  return `${(minutes / 60).toFixed(1)}h`
}

/**
 * A verdict as its full sentence, hours included: `'clean fit'`,
 * `'9.5h rest before your AM'`, `'overlaps your N, ends 07:00'`,
 * `'you already work a D that day'`, `"you're on leave that day"`.
 */
export function formatFitVerdict(verdict: FitVerdict, config: RosterConfig): string {
  switch (verdict.kind) {
    case 'CLEAN':
      return 'clean fit'
    case 'WARN':
      return `${formatHours(verdict.gapMin)} rest ${verdict.before ? 'before' : 'after'} your ${verdict.otherCode}`
    case 'BLOCK':
      switch (verdict.reason) {
        case 'SAME_DAY':
          return `you already work a ${verdict.otherCode} that day`
        case 'EXCEPTION':
          switch (verdict.exceptionKind) {
            case 'LEAVE':
              return "you're on leave that day"
            case 'UNAVAILABLE':
              return "you're unavailable that day"
            case 'NOT_WORKING':
              return "you've marked that day not working"
          }
          break
        case 'OVERLAP': {
          const other = rosterShiftFor(verdict.otherCode, config)
          const boundary = verdict.viewerShiftEarlier
            ? `ends ${formatMinutes(other.endMin)}`
            : `starts ${formatMinutes(other.startMin)}`
          return `overlaps your ${verdict.otherCode}, ${boundary}`
        }
      }
  }
}

/** The badge text: clean and warn verdicts verbatim, blocks prefixed with the verdict. */
export function fitBadgeText(verdict: FitVerdict, config: RosterConfig): string {
  const sentence = formatFitVerdict(verdict, config)
  return verdict.kind === 'BLOCK' ? `can't — ${sentence}` : sentence
}

/** `config.shifts` lookup, throwing the engine's own way on an unknown code. */
function rosterShiftFor(code: ShiftCode, config: RosterConfig): RosterShift {
  const found = config.shifts.find((candidate) => candidate.code === code)
  if (found === undefined) {
    throw new RangeError(`No roster shift configured for code ${JSON.stringify(code)}`)
  }
  return found
}
